import { c as _c } from "react/compiler-runtime";
import type { TenantScope } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import type { WithoutEnvironments } from '../utils/scope-environments';

// These aren't sent to the API - just used to represent all the options of decryptable things in
// the MultiSelect. We need this since different decrypt options have special serializations in the API
export enum DecryptOption {
  all = 'all',
  name = 'name',
  email = 'email',
  phoneNumber = 'phone_number',
  usTaxId = 'us_tax_id',
  ssn4 = 'ssn4',
  dob = 'dob',
  documents = 'documents',
  documentsAndSelfie = 'documents_and_selfie',
  fullAddress = 'full_address',
  custom = 'custom',
  investorProfile = 'investor_profile',
  usLegalStatus = 'us_legal_status',
  card = 'card',
  allExceptPciData = 'all_except_pci_data',
  businessName = 'business_name',
  businessTin = 'business_tin',
  businessAddress = 'business_address',
  businessPhoneNumber = 'business_phone_number',
  businessWebsite = 'business_website',
  businessKycedBeneficialOwners = 'business_kyced_beneficial_owners',
  businessCorporationType = 'business_corporation_type',
  businessFormationInfo = 'business_formation_info',
}
export const decryptOptionFromTenantScope = (scope: TenantScope): DecryptOption | undefined => {
  if (scope.kind === 'decrypt_all') {
    return DecryptOption.all;
  }
  if (scope.kind === 'decrypt_all_except_pci_data') {
    return DecryptOption.allExceptPciData;
  }
  if (scope.kind === 'decrypt_custom') {
    return DecryptOption.custom;
  }
  if (scope.kind === 'decrypt_document') {
    return DecryptOption.documents;
  }
  if (scope.kind === 'decrypt_document_and_selfie') {
    return DecryptOption.documentsAndSelfie;
  }
  if (scope.kind === 'decrypt') {
    if (scope.data === 'email') {
      return DecryptOption.email;
    }
    if (scope.data === 'us_tax_id') {
      return DecryptOption.usTaxId;
    }
    if (scope.data === 'dob') {
      return DecryptOption.dob;
    }
    if (scope.data === 'full_address') {
      return DecryptOption.fullAddress;
    }
    if (scope.data === 'us_legal_status') {
      return DecryptOption.usLegalStatus;
    }
    if (scope.data === 'card') {
      return DecryptOption.card;
    }
    if (scope.data === 'investor_profile') {
      return DecryptOption.investorProfile;
    }
    if (scope.data === 'name') {
      return DecryptOption.name;
    }
    if (scope.data === 'phone_number') {
      return DecryptOption.phoneNumber;
    }
    if (scope.data === 'ssn4') {
      return DecryptOption.ssn4;
    }
    if (scope.data === 'business_name') {
      return DecryptOption.businessName;
    }
    if (scope.data === 'business_tin') {
      return DecryptOption.businessTin;
    }
    if (scope.data === 'business_address') {
      return DecryptOption.businessAddress;
    }
    if (scope.data === 'business_phone_number') {
      return DecryptOption.businessPhoneNumber;
    }
    if (scope.data === 'business_website') {
      return DecryptOption.businessWebsite;
    }
    if (scope.data === 'business_kyced_beneficial_owners') {
      return DecryptOption.businessKycedBeneficialOwners;
    }
    if (scope.data === 'business_corporation_type') {
      return DecryptOption.businessCorporationType;
    }
    if (scope.data === 'business_formation_info') {
      return DecryptOption.businessFormationInfo;
    }
  }
  return undefined;
};
export const DecryptOptionToTenantScope: Record<DecryptOption, WithoutEnvironments<TenantScope>> = {
  [DecryptOption.all]: {
    kind: 'decrypt_all'
  },
  [DecryptOption.name]: {
    kind: 'decrypt',
    data: 'name'
  },
  [DecryptOption.email]: {
    kind: 'decrypt',
    data: 'email'
  },
  [DecryptOption.phoneNumber]: {
    kind: 'decrypt',
    data: 'phone_number'
  },
  [DecryptOption.usTaxId]: {
    kind: 'decrypt',
    data: 'us_tax_id'
  },
  [DecryptOption.ssn4]: {
    kind: 'decrypt',
    data: 'ssn4'
  },
  [DecryptOption.dob]: {
    kind: 'decrypt',
    data: 'dob'
  },
  [DecryptOption.documents]: {
    kind: 'decrypt_document'
  },
  [DecryptOption.documentsAndSelfie]: {
    kind: 'decrypt_document_and_selfie'
  },
  [DecryptOption.fullAddress]: {
    kind: 'decrypt',
    data: 'full_address'
  },
  [DecryptOption.usLegalStatus]: {
    kind: 'decrypt',
    data: 'us_legal_status'
  },
  [DecryptOption.custom]: {
    kind: 'decrypt_custom'
  },
  [DecryptOption.investorProfile]: {
    kind: 'decrypt',
    data: 'investor_profile'
  },
  [DecryptOption.card]: {
    kind: 'decrypt',
    data: 'card'
  },
  [DecryptOption.allExceptPciData]: {
    kind: 'decrypt_all_except_pci_data'
  },
  [DecryptOption.businessName]: {
    kind: 'decrypt',
    data: 'business_name'
  },
  [DecryptOption.businessCorporationType]: {
    kind: 'decrypt',
    data: 'business_corporation_type'
  },
  [DecryptOption.businessTin]: {
    kind: 'decrypt',
    data: 'business_tin'
  },
  [DecryptOption.businessWebsite]: {
    kind: 'decrypt',
    data: 'business_website'
  },
  [DecryptOption.businessPhoneNumber]: {
    kind: 'decrypt',
    data: 'business_phone_number'
  },
  [DecryptOption.businessAddress]: {
    kind: 'decrypt',
    data: 'business_address'
  },
  [DecryptOption.businessKycedBeneficialOwners]: {
    kind: 'decrypt',
    data: 'business_kyced_beneficial_owners'
  },
  [DecryptOption.businessFormationInfo]: {
    kind: 'decrypt',
    data: 'business_formation_info'
  }
};
type Option = {
  label: string;
  value: DecryptOption;
};
type Group = {
  options: readonly Option[];
  label?: string;
};
const useDecryptOptions = () => {
  const $ = _c(134);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "scopes"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("roles", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      value: DecryptOption.name,
      label: t1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t(_temp2);
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = {
      value: DecryptOption.dob,
      label: t3
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t(_temp3);
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = {
      value: DecryptOption.usTaxId,
      label: t5
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t(_temp4);
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = {
      value: DecryptOption.ssn4,
      label: t7
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t(_temp5);
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = {
      value: DecryptOption.fullAddress,
      label: t9
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t(_temp6);
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = {
      value: DecryptOption.email,
      label: t11
    };
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t) {
    t13 = t(_temp7);
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = {
      value: DecryptOption.phoneNumber,
      label: t13
    };
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t14 || $[32] !== t2 || $[33] !== t4 || $[34] !== t6 || $[35] !== t8) {
    t15 = {
      label: "User identity data",
      options: [t2, t4, t6, t8, t10, t12, t14]
    };
    $[29] = t10;
    $[30] = t12;
    $[31] = t14;
    $[32] = t2;
    $[33] = t4;
    $[34] = t6;
    $[35] = t8;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t) {
    t16 = t(_temp8);
    $[37] = t;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16) {
    t17 = {
      value: DecryptOption.documentsAndSelfie,
      label: t16
    };
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t) {
    t18 = t(_temp9);
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t18) {
    t19 = {
      value: DecryptOption.documents,
      label: t18
    };
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t) {
    t20 = t(_temp10);
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20) {
    t21 = {
      value: DecryptOption.custom,
      label: t20
    };
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t) {
    t22 = t(_temp11);
    $[49] = t;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t22) {
    t23 = {
      value: DecryptOption.investorProfile,
      label: t22
    };
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t) {
    t24 = t(_temp12);
    $[53] = t;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t24) {
    t25 = {
      value: DecryptOption.usLegalStatus,
      label: t24
    };
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t) {
    t26 = t(_temp13);
    $[57] = t;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t26) {
    t27 = {
      value: DecryptOption.card,
      label: t26
    };
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t17 || $[62] !== t19 || $[63] !== t21 || $[64] !== t23 || $[65] !== t25 || $[66] !== t27) {
    t28 = {
      label: "Other user data",
      options: [t17, t19, t21, t23, t25, t27]
    };
    $[61] = t17;
    $[62] = t19;
    $[63] = t21;
    $[64] = t23;
    $[65] = t25;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t) {
    t29 = t(_temp14);
    $[68] = t;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t29) {
    t30 = {
      value: DecryptOption.businessName,
      label: t29
    };
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t) {
    t31 = t(_temp15);
    $[72] = t;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t31) {
    t32 = {
      value: DecryptOption.businessFormationInfo,
      label: t31
    };
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t) {
    t33 = t(_temp16);
    $[76] = t;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t33) {
    t34 = {
      value: DecryptOption.businessCorporationType,
      label: t33
    };
    $[78] = t33;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== t) {
    t35 = t(_temp17);
    $[80] = t;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== t35) {
    t36 = {
      value: DecryptOption.businessTin,
      label: t35
    };
    $[82] = t35;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  let t37;
  if ($[84] !== t) {
    t37 = t(_temp18);
    $[84] = t;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== t37) {
    t38 = {
      value: DecryptOption.businessAddress,
      label: t37
    };
    $[86] = t37;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== t) {
    t39 = t(_temp19);
    $[88] = t;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== t39) {
    t40 = {
      value: DecryptOption.businessWebsite,
      label: t39
    };
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== t) {
    t41 = t(_temp20);
    $[92] = t;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== t41) {
    t42 = {
      value: DecryptOption.businessPhoneNumber,
      label: t41
    };
    $[94] = t41;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== t) {
    t43 = t(_temp21);
    $[96] = t;
    $[97] = t43;
  } else {
    t43 = $[97];
  }
  let t44;
  if ($[98] !== t43) {
    t44 = {
      value: DecryptOption.businessKycedBeneficialOwners,
      label: t43
    };
    $[98] = t43;
    $[99] = t44;
  } else {
    t44 = $[99];
  }
  let t45;
  if ($[100] !== t30 || $[101] !== t32 || $[102] !== t34 || $[103] !== t36 || $[104] !== t38 || $[105] !== t40 || $[106] !== t42 || $[107] !== t44) {
    t45 = {
      label: "Business data",
      options: [t30, t32, t34, t36, t38, t40, t42, t44]
    };
    $[100] = t30;
    $[101] = t32;
    $[102] = t34;
    $[103] = t36;
    $[104] = t38;
    $[105] = t40;
    $[106] = t42;
    $[107] = t44;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  let t46;
  if ($[109] !== t15 || $[110] !== t28 || $[111] !== t45) {
    t46 = [t15, t28, t45];
    $[109] = t15;
    $[110] = t28;
    $[111] = t45;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  const options = t46;
  let t47;
  if ($[113] !== t) {
    t47 = t(_temp22);
    $[113] = t;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== t47) {
    t48 = {
      value: DecryptOption.all,
      label: t47
    };
    $[115] = t47;
    $[116] = t48;
  } else {
    t48 = $[116];
  }
  const allOption = t48;
  let t49;
  if ($[117] !== t) {
    t49 = t(_temp23);
    $[117] = t;
    $[118] = t49;
  } else {
    t49 = $[118];
  }
  let t50;
  if ($[119] !== t49) {
    t50 = {
      value: DecryptOption.allExceptPciData,
      label: t49
    };
    $[119] = t49;
    $[120] = t50;
  } else {
    t50 = $[120];
  }
  const allExceptPciDataOption = t50;
  let t51;
  if ($[121] !== options) {
    t51 = options.flatMap(_temp24);
    $[121] = options;
    $[122] = t51;
  } else {
    t51 = $[122];
  }
  let t52;
  if ($[123] !== allExceptPciDataOption || $[124] !== allOption || $[125] !== t51) {
    t52 = new Map([allOption, allExceptPciDataOption, ...t51].map(_temp25));
    $[123] = allExceptPciDataOption;
    $[124] = allOption;
    $[125] = t51;
    $[126] = t52;
  } else {
    t52 = $[126];
  }
  const labels = t52;
  let t53;
  if ($[127] !== labels) {
    t53 = option_0 => labels.get(option_0) ?? option_0;
    $[127] = labels;
    $[128] = t53;
  } else {
    t53 = $[128];
  }
  const getLabel = t53;
  let t54;
  if ($[129] !== allExceptPciDataOption || $[130] !== allOption || $[131] !== getLabel || $[132] !== options) {
    t54 = {
      options,
      allOption,
      allExceptPciDataOption,
      getLabel
    };
    $[129] = allExceptPciDataOption;
    $[130] = allOption;
    $[131] = getLabel;
    $[132] = options;
    $[133] = t54;
  } else {
    t54 = $[133];
  }
  return t54;
};
export default useDecryptOptions;
function _temp($) {
  return $.decrypt.name;
}
function _temp2($_0) {
  return $_0.decrypt.dob;
}
function _temp3($_1) {
  return $_1.decrypt.us_tax_id;
}
function _temp4($_2) {
  return $_2.decrypt.ssn4;
}
function _temp5($_3) {
  return $_3.decrypt.full_address;
}
function _temp6($_4) {
  return $_4.decrypt.email;
}
function _temp7($_5) {
  return $_5.decrypt.phone_number;
}
function _temp8($_6) {
  return $_6.decrypt.documents_and_selfie;
}
function _temp9($_7) {
  return $_7.decrypt.documents;
}
function _temp10($_8) {
  return $_8.decrypt.custom;
}
function _temp11($_9) {
  return $_9.decrypt.investor_profile;
}
function _temp12($_10) {
  return $_10.decrypt.us_legal_status;
}
function _temp13($_11) {
  return $_11.decrypt.card;
}
function _temp14($_12) {
  return $_12.decrypt.business_name;
}
function _temp15($_13) {
  return $_13.decrypt.business_formation_info;
}
function _temp16($_14) {
  return $_14.decrypt.business_corporation_type;
}
function _temp17($_15) {
  return $_15.decrypt.business_tin;
}
function _temp18($_16) {
  return $_16.decrypt.business_address;
}
function _temp19($_17) {
  return $_17.decrypt.business_website;
}
function _temp20($_18) {
  return $_18.decrypt.business_phone_number;
}
function _temp21($_19) {
  return $_19.decrypt.business_kyced_beneficial_owners;
}
function _temp22($_20) {
  return $_20.decrypt_all;
}
function _temp23($_21) {
  return $_21.decrypt_all_except_pci_data;
}
function _temp24(group) {
  return group.options;
}
function _temp25(option) {
  return [option.value, option.label];
}